import { Grid, TextField } from '@mui/material'
import { PinInfo } from '@planning/rt-stores/railTrack/RailcarTrackPositionItem'

import {
  AssignRailcarTrackPositionCommand,
  CheckType,
  RailcarTrackPositionResponseDto,
  RailTrackResponseDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import RailTrackControlledAutocomplete from '@planning/pages/Order/components/RailTrackControlledAutocomplete'
import { railVisitService } from '@planning/services'
import { useNavigationStore } from '@planning/stores'
import { formatRailcarName } from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment } from '@tom-ui/ui'
import {
  notAllowPlusMinusPointSign,
  notAllowPlusMinusSign,
  onlyAllowPositiveInt,
} from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { Controller, FieldError, useFormContext } from 'react-hook-form'
import { IInspectRailcarFormData } from '../../InspectRailcar'
import { InspectRailcarPins } from './InspectRailcarPins'

interface IProps {
  railcarTrackPosition: RailcarTrackPositionResponseDto
  operationType?: CheckType
  pins: PinInfo[]
  isReadOnly?: boolean
  nonCheckedSequenceNumbers?: number[]
}

export const InspectRailcarForm: FC<IProps> = observer(
  ({ railcarTrackPosition, operationType, pins, isReadOnly, nonCheckedSequenceNumbers }) => {
    const { t } = useTranslate()
    const { railVisitItemStore, railcarTrackPositionItemStore, appViewStore } = usePlanningStore()
    const navigationStore = useNavigationStore()

    const {
      id: railcarTrackPositionId,
      railVisitId,
      railTrackId,
      railcarName,
      railcarSequenceNumber,
    } = railcarTrackPosition

    const {
      control,
      setValue,
      register,
      formState: { errors },
    } = useFormContext<IInspectRailcarFormData>()

    const getRailTracksByVisit = useCallback(
      (visitId: number) => {
        const railTracks = railcarTrackPositionItemStore.getRailTracks()

        const assignedRailTrackIds =
          railVisitItemStore.getById(visitId)?.data.assignedRailTrackIds || []

        return railTracks.filter(x => assignedRailTrackIds.includes(x.id))
      },
      [railVisitItemStore, railcarTrackPositionItemStore],
    )

    const railTracks = computed(() => getRailTracksByVisit(railVisitId)).get()

    const onChange = async (railTrack: RailTrackResponseDto | null) => {
      if (railTrackId !== railTrack?.id) {
        const confirm = await appViewStore.setOpenConfirmDialog(
          true,
          t(
            'willRecordThisRailcarAgainsTrackForCheckingLate',
            'We will record this railcar against {trackName} for checking later.',
            {
              trackName: railTrack?.name,
            },
          ),
          t('changeTrack', 'Change Track'),
        )

        if (confirm && railTrack?.id) {
          const cmd: AssignRailcarTrackPositionCommand = {
            railVisitId,
            ids: [railcarTrackPositionId],
            newTrackId: railTrack.id,
          }

          appViewStore.showLoader()

          try {
            await railVisitService.assignRailcarTrackPosition(cmd)
            appViewStore.setShowAlert(
              'success',
              t('railcarMovedToTrack', 'Railcar {railcar} was moved to track {track}', {
                railcar: formatRailcarName(railcarName),
                track: railTrack?.name,
              }),
            )
            navigationStore.pop()
          } catch (e) {
            appViewStore.setShowAlert('error', t('failedToMoveRailcar', 'Failed to move railcar'))
          } finally {
            appViewStore.hideLoader()
          }
        } else {
          setValue('railTrackId', railTrackId)
        }
      }
    }

    const getSequenceHelperText = (sequenceError?: FieldError) => {
      if (!sequenceError) return ''

      if (sequenceError.type === 'required') {
        return t('fieldIsRequired', 'Field is required.')
      }

      if (sequenceError.type === 'min') {
        return t('mustBeAtLeastOne', 'Must be at least 1.')
      }

      if (sequenceError.type === 'validate') {
        return `${t(
          'sequenceNumberAlreadyCheckedError',
          'Sequence number has already been checked for this rail track.',
        )} ${
          nonCheckedSequenceNumbers?.length
            ? t('availableSequenceNumbers', 'Available sequence numbers: {sequenceNumbers}', {
                sequenceNumbers: nonCheckedSequenceNumbers.join(', '),
              })
            : ''
        }`
      }

      return t('unknownError', 'Unknown error')
    }

    return (
      <form>
        <Grid container spacing='0.5rem' p='1rem'>
          <Grid size={12}>
            <RailTrackControlledAutocomplete
              control={control}
              errors={errors}
              setValue={setValue}
              onChangeFn={onChange}
              name='railTrackId'
              label={t('railTrack', 'Rail Track')}
              options={railTracks}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name='sequence'
              rules={{
                required: true,
                validate: value => {
                  return (
                    !nonCheckedSequenceNumbers?.length || nonCheckedSequenceNumbers?.includes(value)
                  )
                },
              }}
              render={() => (
                <>
                  <TextField
                    data-cy='railcar-sequence'
                    fullWidth
                    label={`${t('sequenceNo', 'Sequence no.')}`}
                    required
                    variant='outlined'
                    type='number'
                    {...register('sequence', { valueAsNumber: true, min: 1 })}
                    name='sequence'
                    defaultValue={railcarSequenceNumber}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={onlyAllowPositiveInt}
                    error={!!errors.sequence}
                    helperText={getSequenceHelperText(errors.sequence)}
                    sx={{ marginTop: '1rem' }}
                    disabled={isReadOnly}
                  />
                </>
              )}
            ></Controller>
          </Grid>
          <Grid size={12}>
            <TextField
              data-cy='railcar-length'
              fullWidth
              label={`${t('length', 'Length')}`}
              variant='outlined'
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <CustomInputAdornment position='end' color='secondary'>
                      m
                    </CustomInputAdornment>
                  ),
                },
                htmlInput: {
                  min: '0',
                  step: '0.01',
                  pattern: '[0-9]*',
                },
              }}
              {...register('length', { valueAsNumber: true })}
              onKeyDown={notAllowPlusMinusSign}
              name='length'
              sx={{ marginTop: '1rem' }}
              disabled={isReadOnly}
            />
          </Grid>

          {operationType === CheckType.LoadPreparation && pins && (
            <Grid size={12}>
              <InspectRailcarPins pins={pins} />
            </Grid>
          )}
        </Grid>
      </form>
    )
  },
)
